import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  XMarkIcon,
  PhotoIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
  ClockIcon,
  TagIcon,
  BoltIcon,
  CogIcon,
  ShieldCheckIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

const EquipmentForm = ({ isOpen, onClose, editData = null, isModal = false }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Déterminer si nous sommes en mode édition
  const isEditing = isModal ? !!editData : !!id;
  
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    model: '',
    category: 'strength',
    serialNumber: '',
    purchaseDate: '',
    purchasePrice: '',
    warrantyExpiry: '',
    location: '',
    description: '',
    photo: null,
    photoPreview: null,
    status: 'available',
    maintenanceInterval: '30', // days
    lastMaintenance: '',
    nextMaintenance: '',
    notes: '',
    specifications: [''],
    accessories: [''],
  });

  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(isEditing && !isModal);
  const [errors, setErrors] = useState({});
  // Modify the useState hooks to include formSubmitted state
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Charger les données d'équipement si on est en mode édition (route)
  useEffect(() => {
    if (isEditing && !isModal && id) {
      setLoading(true);
      axios.get(`/equipments/${id}`)
        .then(response => {
          const equipmentData = response.data;
          setFormData({
            ...equipmentData,
            specifications: equipmentData.specifications || [''],
            accessories: equipmentData.accessories || [''],
            purchaseDate: equipmentData.purchaseDate ? new Date(equipmentData.purchaseDate).toISOString().split('T')[0] : '',
            warrantyExpiry: equipmentData.warrantyExpiry ? new Date(equipmentData.warrantyExpiry).toISOString().split('T')[0] : '',
            lastMaintenance: equipmentData.lastMaintenance ? new Date(equipmentData.lastMaintenance).toISOString().split('T')[0] : '',
            nextMaintenance: equipmentData.nextMaintenance ? new Date(equipmentData.nextMaintenance).toISOString().split('T')[0] : '',
            photoPreview: equipmentData.photo || null,
          });
          setLoading(false);
        })
        .catch(error => {
          toast.error('Erreur lors du chargement de l\'équipement');
          navigate('/equipment');
          setLoading(false);
        });
    }
  }, [id, isEditing, isModal]);

  // Equipment categories - update to match backend exactly
  const categories = [
    { id: 'cardio', name: 'Cardio', icon: BoltIcon, type: 'cardio' },
    { id: 'strength', name: 'Musculation', icon: CogIcon, type: 'strength' },
    { id: 'functional', name: 'Fonctionnel', icon: ShieldCheckIcon, type: 'functional' },
    { id: 'accessories', name: 'Accessoires', icon: WrenchScrewdriverIcon, type: 'accessories' }
  ];

  // Status options - update to match backend exactly
  const statusOptions = [
    { id: 'active', name: 'Disponible', color: 'green' },
    { id: 'maintenance', name: 'En maintenance', color: 'yellow' },
    { id: 'out-of-order', name: 'Hors service', color: 'red' }
  ];

  // Create/Update equipment mutation
  const createMutation = useMutation(
    (equipmentData) => {
      return axios.post('/equipments', equipmentData, {
        headers: { 'Content-Type': 'application/json' }
      });
    },
    {
      onSuccess: () => {
        toast.success('Équipement ajouté avec succès!');
        queryClient.invalidateQueries('equipment');
        if (isModal) {
          onClose();
        } else {
          navigate('/equipment');
        }
        resetForm();
      },
      onError: (error) => {
        console.error('Error creating equipment:', error.response?.data || error);
        toast.error('Erreur lors de l\'ajout: ' + (error.response?.data?.message || 'Vérifiez les champs obligatoires'));
      },
    }
  );

  const updateMutation = useMutation(
    (equipmentData) => {
      const equipmentId = isModal ? editData.id : id;
      return axios.put(`/equipments/${equipmentId}`, equipmentData, {
        headers: { 'Content-Type': 'application/json' }
      });
    },
    {
      onSuccess: () => {
        toast.success('Équipement modifié avec succès!');
        queryClient.invalidateQueries('equipment');
        if (isModal) {
          onClose();
        } else {
          navigate('/equipment');
        }
        resetForm();
      },
      onError: (error) => {
        console.error('Error updating equipment:', error.response?.data || error);
        toast.error('Erreur lors de la modification: ' + (error.response?.data?.message || 'Vérifiez les champs obligatoires'));
      },
    }
  );

  // Update form data when editing in modal
  useEffect(() => {
    if (editData && isModal) {
      setFormData({
        ...editData,
        specifications: editData.specifications || [''],
        accessories: editData.accessories || [''],
        purchaseDate: editData.purchaseDate ? new Date(editData.purchaseDate).toISOString().split('T')[0] : '',
        warrantyExpiry: editData.warrantyExpiry ? new Date(editData.warrantyExpiry).toISOString().split('T')[0] : '',
        lastMaintenance: editData.lastMaintenance ? new Date(editData.lastMaintenance).toISOString().split('T')[0] : '',
        nextMaintenance: editData.nextMaintenance ? new Date(editData.nextMaintenance).toISOString().split('T')[0] : '',
        photoPreview: editData.photo || null,
      });
    }
  }, [editData, isModal]);

  const resetForm = () => {
    setFormData({
      name: '',
      brand: '',
      model: '',
      category: 'strength',
      serialNumber: '',
      purchaseDate: '',
      purchasePrice: '',
      warrantyExpiry: '',
      location: '',
      description: '',
      photo: null,
      photoPreview: null,
      status: 'available',
      maintenanceInterval: '30',
      lastMaintenance: '',
      nextMaintenance: '',
      notes: '',
      specifications: [''],
      accessories: [''],
    });
    setErrors({}); // Clear errors on reset
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Auto-calculate next maintenance date
    if (name === 'lastMaintenance' && value) {
      const lastMaintenanceDate = new Date(value);
      const intervalDays = parseInt(formData.maintenanceInterval) || 30;
      const nextMaintenanceDate = new Date(lastMaintenanceDate);
      nextMaintenanceDate.setDate(nextMaintenanceDate.getDate() + intervalDays);
      
      setFormData(prev => ({
        ...prev,
        nextMaintenance: nextMaintenanceDate.toISOString().split('T')[0],
      }));
    }
  };

  const handlePhotoUpload = (files) => {
    const file = files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('La taille de l\'image doit être inférieure à 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('Veuillez sélectionner une image valide');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          photo: file,
          photoPreview: e.target.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handlePhotoUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleArrayFieldChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData(prev => ({
      ...prev,
      [field]: newArray,
    }));
  };

  const addArrayField = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], ''],
    }));
  };

  const removeArrayField = (field, index) => {
    if (formData[field].length > 1) {
      const newArray = formData[field].filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        [field]: newArray,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    if (!formData.name.trim()) {
      newErrors.name = "Nom de l'équipement est obligatoire";
    }
    
    if (!formData.brand.trim()) {
      newErrors.brand = "Marque est obligatoire";
    }
    
    // Purchase price validation if entered
    if (formData.purchasePrice && (isNaN(formData.purchasePrice) || Number(formData.purchasePrice) < 0)) {
      newErrors.purchasePrice = "Prix d'achat doit être un nombre positif";
    }
    
    // Date validations
    const currentDate = new Date();
    const purchaseDate = formData.purchaseDate ? new Date(formData.purchaseDate) : null;
    const warrantyDate = formData.warrantyExpiry ? new Date(formData.warrantyExpiry) : null;
    
    if (purchaseDate && purchaseDate > currentDate) {
      newErrors.purchaseDate = "La date d'achat ne peut pas être dans le futur";
    }
    
    if (purchaseDate && warrantyDate && warrantyDate < purchaseDate) {
      newErrors.warrantyExpiry = "La garantie ne peut pas expirer avant la date d'achat";
    }
    
    // Maintenance interval validation
    if (formData.maintenanceInterval && (isNaN(formData.maintenanceInterval) || Number(formData.maintenanceInterval) < 1)) {
      newErrors.maintenanceInterval = "L'intervalle doit être un nombre positif";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Create a mapping function to convert category to valid type values
  const mapCategoryToType = (category) => {
    const categoryToTypeMap = {
      'cardio': 'cardio',
      'strength': 'strength',
      'functional': 'functional',
      'accessories': 'accessories'
    };
    // Return the mapped value or a default accepted by the backend
    return categoryToTypeMap[category] || 'other';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    
    if (!validateForm()) {
      // Scroll to the first error
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      return;
    }

    // Get the matching type for the selected category
    const selectedCategory = categories.find(cat => cat.id === formData.category);
    
    // Create a simplified payload with only the essential fields
    const simplifiedData = {
      name: formData.name,
      brand: formData.brand,
      model: formData.model || '',
      category: formData.category,
      type: selectedCategory ? selectedCategory.type : 'other', // Use the exact type from our mapping
      status: formData.status === 'available' ? 'active' : formData.status, // Map available to active for backend
      serialNumber: formData.serialNumber || '',
      description: formData.description || '',
      // Include other fields as needed
    };

    // Log what we're actually sending
    console.log('Submitting equipment with simplified data:', simplifiedData);

    if (isEditing) {
      updateMutation.mutate(simplifiedData);
    } else {
      createMutation.mutate(simplifiedData);
    }
  };

  // Create an error summary component at the top of the form
  const ErrorSummary = () => {
    const errorCount = Object.keys(errors).length;
    
    if (errorCount === 0 || !formSubmitted) return null;
    
    return (
      <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 mb-6">
        <h3 className="text-red-400 font-medium text-sm mb-2">
          {errorCount === 1 
            ? 'Il y a 1 erreur dans le formulaire' 
            : `Il y a ${errorCount} erreurs dans le formulaire`}
        </h3>
        <ul className="text-red-400 text-xs space-y-1">
          {Object.keys(errors).map(field => (
            <li key={field} className="flex items-center">
              <span className="mr-2">•</span>
              <span>{errors[field]}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Render form content
  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-6">
      <ErrorSummary />
      {/* Photo Upload */}
      <div className="mb-6">
        <label className="block text-white font-medium mb-2">Photo de l'équipement</label>
        <div
          className={`border-2 border-dashed rounded-3xl p-6 text-center transition-colors ${
            dragActive ? 'border-blue-400 bg-blue-400/10' : 'border-white/20 hover:border-white/40'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {formData.photoPreview ? (
            <div className="relative">
              <img
                src={formData.photoPreview}
                alt="Preview"
                className="max-h-64 mx-auto rounded-xl"
              />
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, photo: null, photoPreview: null }))}
                className="absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <PhotoIcon className="h-12 w-12 text-white/40 mx-auto" />
              <p className="text-white/60">Glissez une photo ici ou cliquez pour sélectionner</p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handlePhotoUpload(e.target.files)}
                className="hidden"
                id="photo-upload"
              />
              <label
                htmlFor="photo-upload"
                className="inline-block bg-blue-500 text-white px-4 py-2 rounded-xl hover:bg-blue-600 transition-colors cursor-pointer"
              >
                Choisir une photo
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-white font-medium mb-2">
            Nom de l'équipement *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full p-3 bg-white/10 border ${errors.name ? 'border-red-500' : 'border-white/20'} rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 ${errors.name ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
            placeholder="Ex: Tapis de course professionnel"
            required
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name}</p>
          )}
        </div>

        <div>
          <label className="block text-white font-medium mb-2">
            Marque *
          </label>
          <input
            type="text"
            name="brand"
            value={formData.brand}
            onChange={handleInputChange}
            className={`w-full p-3 bg-white/10 border ${errors.brand ? 'border-red-500' : 'border-white/20'} rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 ${errors.brand ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
            placeholder="Ex: TechnogGym"
            required
          />
          {errors.brand && (
            <p className="text-red-500 text-sm mt-1">{errors.brand}</p>
          )}
        </div>

        <div>
          <label className="block text-white font-medium mb-2">
            Modèle
          </label>
          <input
            type="text"
            name="model"
            value={formData.model}
            onChange={handleInputChange}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: Run Race 1400"
          />
        </div>

        <div>
          <label className="block text-white font-medium mb-2">
            Catégorie *
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-white font-medium mb-2">
            Numéro de série
          </label>
          <input
            type="text"
            name="serialNumber"
            value={formData.serialNumber}
            onChange={handleInputChange}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: TG-2024-001"
          />
        </div>

        <div>
          <label className="block text-white font-medium mb-2">
            Emplacement
          </label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: Zone cardio - Rangée 1"
          />
        </div>

        <div>
          <label className="block text-white font-medium mb-2">
            Date d'achat
          </label>
          <input
            type="date"
            name="purchaseDate"
            value={formData.purchaseDate}
            onChange={handleInputChange}
            className={`w-full p-3 bg-white/10 border ${errors.purchaseDate ? 'border-red-500' : 'border-white/20'} rounded-xl text-white focus:outline-none focus:ring-2 ${errors.purchaseDate ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
          />
          {errors.purchaseDate && (
            <p className="text-red-500 text-sm mt-1">{errors.purchaseDate}</p>
          )}
        </div>

        <div>
          <label className="block text-white font-medium mb-2">
            Prix d'achat (DA)
          </label>
          <input
            type="number"
            name="purchasePrice"
            value={formData.purchasePrice}
            onChange={handleInputChange}
            className={`w-full p-3 bg-white/10 border ${errors.purchasePrice ? 'border-red-500' : 'border-white/20'} rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 ${errors.purchasePrice ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
            placeholder="Ex: 250000"
            min="0"
            step="100"
          />
          {errors.purchasePrice && (
            <p className="text-red-500 text-sm mt-1">{errors.purchasePrice}</p>
          )}
        </div>

        <div>
          <label className="block text-white font-medium mb-2">
            Expiration de la garantie
          </label>
          <input
            type="date"
            name="warrantyExpiry"
            value={formData.warrantyExpiry}
            onChange={handleInputChange}
            className={`w-full p-3 bg-white/10 border ${errors.warrantyExpiry ? 'border-red-500' : 'border-white/20'} rounded-xl text-white focus:outline-none focus:ring-2 ${errors.warrantyExpiry ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
          />
          {errors.warrantyExpiry && (
            <p className="text-red-500 text-sm mt-1">{errors.warrantyExpiry}</p>
          )}
        </div>

        <div>
          <label className="block text-white font-medium mb-2">
            Statut
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {statusOptions.map(status => (
              <option key={status.id} value={status.id} className="bg-gray-800">
                {status.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Maintenance Information */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <WrenchScrewdriverIcon className="h-5 w-5 mr-2" />
          Maintenance
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-white font-medium mb-2">
              Intervalle de maintenance (jours)
            </label>
            <input
              type="number"
              name="maintenanceInterval"
              value={formData.maintenanceInterval}
              onChange={handleInputChange}
              className={`w-full p-3 bg-white/10 border ${errors.maintenanceInterval ? 'border-red-500' : 'border-white/20'} rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 ${errors.maintenanceInterval ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
              placeholder="30"
              min="1"
            />
            {errors.maintenanceInterval && (
              <p className="text-red-500 text-sm mt-1">{errors.maintenanceInterval}</p>
            )}
          </div>
          <div>
            <label className="block text-white font-medium mb-2">
              Dernière maintenance
            </label>
            <input
              type="date"
              name="lastMaintenance"
              value={formData.lastMaintenance}
              onChange={handleInputChange}
              className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-white font-medium mb-2">
              Prochaine maintenance
            </label>
            <input
              type="date"
              name="nextMaintenance"
              value={formData.nextMaintenance}
              onChange={handleInputChange}
              className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-white font-medium mb-2">
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows="3"
          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          placeholder="Description détaillée de l'équipement..."
        />
      </div>

      {/* Specifications */}
      <div>
        <label className="block text-white font-medium mb-2">
          Spécifications techniques
        </label>
        <div className="space-y-2">
          {formData.specifications.map((spec, index) => (
            <div key={index} className="flex items-center space-x-2">
              <input
                type="text"
                value={spec}
                onChange={(e) => handleArrayFieldChange('specifications', index, e.target.value)}
                className="flex-1 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Vitesse max: 20 km/h"
              />
              <button
                type="button"
                onClick={() => removeArrayField('specifications', index)}
                className="p-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => addArrayField('specifications')}
            className="text-blue-400 hover:text-blue-300 transition-colors text-sm"
          >
            + Ajouter une spécification
          </button>
        </div>
      </div>

      {/* Accessories */}
      <div>
        <label className="block text-white font-medium mb-2">
          Accessoires inclus
        </label>
        <div className="space-y-2">
          {formData.accessories.map((accessory, index) => (
            <div key={index} className="flex items-center space-x-2">
              <input
                type="text"
                value={accessory}
                onChange={(e) => handleArrayFieldChange('accessories', index, e.target.value)}
                className="flex-1 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Clé de réglage"
              />
              <button
                type="button"
                onClick={() => removeArrayField('accessories', index)}
                className="p-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => addArrayField('accessories')}
            className="text-blue-400 hover:text-blue-300 transition-colors text-sm"
          >
            + Ajouter un accessoire
          </button>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-white font-medium mb-2">
          Notes
        </label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleInputChange}
          rows="2"
          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          placeholder="Notes supplémentaires..."
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 mt-6">
        <button
          type="button"
          onClick={isModal ? onClose : () => navigate('/equipment')}
          className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={createMutation.isLoading || updateMutation.isLoading}
          className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all flex items-center space-x-2 disabled:opacity-50"
        >
          {(createMutation.isLoading || updateMutation.isLoading) ? (
            <>
              <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
              <span>{isEditing ? 'Mise à jour...' : 'Création...'}</span>
            </>
          ) : (
            <span>{isEditing ? 'Mettre à jour' : 'Créer'}</span>
          )}
        </button>
      </div>
    </form>
  );

  // Si c'est une modal, retourner le contenu dans une modal
  if (isModal) {
    return (
      <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div className="w-full max-w-4xl bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">
              {isEditing ? 'Modifier l\'équipement' : 'Nouvel équipement'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          
          {formContent}
        </div>
      </div>
    );
  }

  // Sinon, retourner une page complète
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/equipment')}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {isEditing ? 'Modifier l\'équipement' : 'Nouvel équipement'}
            </h1>
            <p className="text-white/60 mt-1">
              {isEditing ? 'Modifier les détails de l\'équipement' : 'Ajouter un nouvel équipement à l\'inventaire'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
          </div>
        ) : formContent}
      </div>
    </div>
  );
};

export default EquipmentForm; 